/**
 * T247.G — Public Find-a-Scorer marketplace listing page.
 *
 * Route: /find-a-scorer/  (no auth required)
 *
 * Browse all LISTED scorer profiles. Filter by region / format /
 * language / verified-only. Card grid → click → detail page.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, BadgeCheck, MapPin, Filter } from "lucide-react";

import { listScorers } from "../../api/scorerMarketplace.api";


const FORMATS = ["T20", "ODI", "TEST", "HUNDRED", "T10", "T15"];

const LANGUAGES = [
  { code: "", label: "Any language" },
  { code: "en", label: "English" },
  { code: "ta", label: "Tamil" },
  { code: "hi", label: "Hindi" },
  { code: "kn", label: "Kannada" },
  { code: "te", label: "Telugu" },
  { code: "ml", label: "Malayalam" },
  { code: "mr", label: "Marathi" },
  { code: "bn", label: "Bengali" },
  { code: "gu", label: "Gujarati" },
];


function ScorerCard({ scorer }) {
  return (
    <Link
      to={`/find-a-scorer/${scorer.id}`}
      className="block border border-bg-secondary rounded-lg p-4 bg-bg-primary hover:bg-bg-secondary/40 hover:border-button-primary/40 transition-colors"
    >
      <div className="flex gap-3">
        {scorer.photo_url
          ? <img src={scorer.photo_url} alt={scorer.display_name}
                 className="w-20 h-20 rounded-full object-cover border border-bg-secondary flex-shrink-0" />
          : <div className="w-20 h-20 rounded-full bg-bg-main border border-bg-secondary flex items-center justify-center text-text-secondary text-xs flex-shrink-0">No photo</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <h3 className="font-semibold truncate text-text-primary">{scorer.display_name}</h3>
            {scorer.has_verified_badge && (
              <BadgeCheck className="w-4 h-4 text-button-primary flex-shrink-0" title="Verified by ZCD" />
            )}
          </div>
          <div className="text-xs text-text-secondary flex items-center gap-1 mb-1">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{(scorer.regions || []).slice(0, 2).join(" · ") || "—"}</span>
          </div>
          <div className="flex flex-wrap gap-1 mb-1">
            {(scorer.formats_scored || []).map((f) => (
              <span key={f} className="text-xs px-2 py-0.5 bg-bg-secondary text-text-primary rounded">{f}</span>
            ))}
          </div>
          <div className="text-xs text-text-secondary">
            {scorer.years_experience} yrs scoring
            {scorer.daily_rate_inr ? ` · ₹${scorer.daily_rate_inr}/day` : ""}
          </div>
        </div>
      </div>
    </Link>
  );
}


export default function FindAScorer() {
  const [scorers, setScorers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("");
  const [format, setFormat] = useState("");
  const [language, setLanguage] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await listScorers({
        region,
        format,
        language,
        verified_only: verifiedOnly ? "true" : "",
      });
      setScorers(data.scorers || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [region, format, language, verifiedOnly]);

  // Client-side name search on top of the server-filtered list.
  const filtered = useMemo(() => {
    if (!search.trim()) return scorers;
    const s = search.toLowerCase();
    return scorers.filter((x) =>
      (x.display_name || "").toLowerCase().includes(s)
    );
  }, [scorers, search]);

  const inputCls =
    "mt-0.5 w-full px-2 py-1.5 text-sm rounded-lg bg-bg-main text-text-primary " +
    "border border-bg-secondary focus:border-button-primary outline-none transition-colors " +
    "placeholder:text-text-secondary/60";

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 text-text-primary">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-text-primary">Find a scorer</h1>
        <p className="text-text-secondary">
          Hire a professional cricket scorer for your tournament. All scorers
          on this list work on ZenCricketData and are paid Elite-tier subscribers.
        </p>
      </div>

      {/* Filters */}
      <div className="border border-bg-secondary rounded-lg p-3 bg-bg-primary mb-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-text-secondary">
          <Filter className="w-4 h-4" /> Filter
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <label className="block">
            <span className="text-xs text-text-secondary">Name</span>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary z-10" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search name"
                     className={`${inputCls} pl-7`} />
            </div>
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">Region</span>
            <input type="text" value={region} onChange={(e) => setRegion(e.target.value)}
                   placeholder="Chennai, Mumbai…"
                   className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">Format</span>
            <select value={format} onChange={(e) => setFormat(e.target.value)}
                    className={inputCls}>
              <option value="">Any format</option>
              {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">Language</span>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}
                    className={inputCls}>
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 mt-5 text-sm text-text-primary">
            <input type="checkbox" checked={verifiedOnly}
                   onChange={(e) => setVerifiedOnly(e.target.checked)}
                   className="accent-button-primary" />
            <BadgeCheck className="w-4 h-4 text-button-primary" />
            Verified only
          </label>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-text-secondary">Loading scorers…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-text-secondary">
          <p className="mb-2">No scorers match your filters.</p>
          <p className="text-xs">Try widening your search or clearing filters.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((s) => <ScorerCard key={s.id} scorer={s} />)}
        </div>
      )}

      <div className="mt-12 pt-6 border-t border-bg-secondary text-center text-sm text-text-secondary">
        Are you a scorer who'd like to be listed here?{" "}
        <Link to="/signup" className="text-button-primary hover:underline">Sign up</Link>{" "}
        or{" "}
        <Link to="/login" className="text-button-primary hover:underline">log in</Link>{" "}
        — the listing comes with the ZCD Elite plan.
      </div>
    </div>
  );
}
